/**
 * ProfilePreview - Live preview of profile configuration
 *
 * Shows a mini preview of how the sidebar and dashboard will look
 * with the current profile settings. Supports real resolver output
 * when available, falling back to local feature data.
 */

import React from 'react';
import { FileText, UserCog, Box } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  MODULE_ICONS, PROFILE_ICONS, ALL_WIDGETS,
} from '../../constants/profile_constants.js';
import { useAppFeatures } from '../../hooks/useAppFeatures.js';

export function ProfilePreview({ formData, resolvedSidebar }) {
  const { t } = useTranslation();
  const {
    name = '',
    color = '#3b82f6',
    icon = 'UserCog',
    nav_style = 'grouped',
    modules = [],
    widgets = [],
  } = formData;

  const ProfileIcon = PROFILE_ICONS[icon] || UserCog;
  const { features, featuresByGroup, groups } = useAppFeatures();

  // Build sidebar preview from resolver output or feature data
  const renderSidebarContent = () => {
    // If we have resolver output, use it for real preview
    if (resolvedSidebar) {
      const resolvedGroups = Array.isArray(resolvedSidebar)
        ? resolvedSidebar
        : Object.entries(resolvedSidebar).map(([code, g]) => ({ code, ...g }));

      return resolvedGroups.map((group) => (
        <div key={group.code || group.label}>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider px-2 py-1">
            {group.label || group.code}
          </div>
          {(group.items || []).map((item, idx) => {
            const id = typeof item === 'string' ? item : (item.id || item.view_code);
            const label = typeof item === 'string'
              ? (features.find(f => f.id === id)?.label || id)
              : (item.label || item.view_name || id);
            const Icon = MODULE_ICONS[id] || Box;
            return (
              <div key={`${id}-${idx}`} className="flex items-center gap-2 px-2 py-1 text-xs text-slate-400 rounded hover:bg-slate-800">
                <Icon size={12} />
                <span>{label}</span>
              </div>
            );
          })}
        </div>
      ));
    }

    // Fallback: build from hook data (DB-driven groups)
    if (nav_style === 'minimal') {
      return (
        <div className="space-y-0.5">
          {modules.map(moduleId => {
            const feature = features.find(f => f.id === moduleId);
            if (!feature) return null;
            const Icon = MODULE_ICONS[moduleId] || FileText;
            return (
              <div key={moduleId} className="flex items-center gap-2 px-2 py-1 text-xs text-slate-400 rounded hover:bg-slate-800">
                <Icon size={12} />
                <span>{feature.label}</span>
              </div>
            );
          })}
        </div>
      );
    }

    // Grouped view from DB nav_groups
    const groupEntries = Object.entries(groups).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
    return groupEntries.map(([groupCode, group]) => {
      const groupFeatures = (featuresByGroup[groupCode] || []).filter(f => modules.includes(f.id));
      if (groupFeatures.length === 0) return null;
      return (
        <div key={groupCode}>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider px-2 py-1">
            {group.label}
          </div>
          {groupFeatures.map(feature => {
            const Icon = MODULE_ICONS[feature.id] || FileText;
            return (
              <div key={feature.id} className="flex items-center gap-2 px-2 py-1 text-xs text-slate-400 rounded hover:bg-slate-800">
                <Icon size={12} />
                <span>{feature.label}</span>
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div className="space-y-4">
      {/* Profile badge preview */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <ProfileIcon size={20} style={{ color }} />
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{name || t('uiProfiles.preview.unnamed')}</div>
          <div className="text-xs text-slate-400">{t('uiProfiles.card.nav', { style: t(`uiProfiles.form.navStyles.${nav_style}.label`, { defaultValue: nav_style }) })}</div>
        </div>
      </div>

      {/* Sidebar preview */}
      <div className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden">
        <div className="p-2 border-b border-slate-700 flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-blue-500/20 flex items-center justify-center">
            <span className="text-[8px] text-blue-400 font-bold">FM</span>
          </div>
          <div>
            <div className="text-xs font-bold text-white">FleetManager</div>
            <div className="text-[10px] text-slate-500">v3.0</div>
          </div>
        </div>

        <div className="p-2 space-y-2 max-h-48 overflow-y-auto">
          {renderSidebarContent()}
        </div>
      </div>

      {/* Dashboard preview */}
      <div>
        <div className="text-xs text-slate-400 mb-2">{t('uiProfiles.preview.dashboardWidgets')}</div>
        <div className="bg-slate-900/50 rounded-lg border border-slate-700 p-2">
          {widgets.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-4">
              {t('uiProfiles.preview.noWidgetSelected')}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-1">
              {widgets.map(widgetId => {
                const widget = ALL_WIDGETS.find(w => w.id === widgetId);
                return (
                  <div
                    key={widgetId}
                    className="aspect-square bg-slate-800 rounded flex items-center justify-center p-1"
                    title={widget?.label}
                  >
                    <span className="text-[8px] text-slate-400 text-center leading-tight">
                      {widget?.label?.split(' ')[0]}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-center gap-4 text-xs text-slate-400 pt-2 border-t border-slate-700">
        <span>{t('uiProfiles.preview.moduleCount', { count: modules.length })}</span>
        <span className="text-slate-600">|</span>
        <span>{t('uiProfiles.preview.widgetCount', { count: widgets.length })}</span>
      </div>
    </div>
  );
}

export default ProfilePreview;
